import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import {
  getProduct,
  createProduct,
  updateProduct,
  deleteProduct,
  addPricing,
  updatePricing,
  deletePricing,
} from "../../api/products";
import { listLocations } from "../../api/locations";
import { listSeasons } from "../../api/seasons";
import {
  DERIVATIONS,
  COST_BASES,
  SLOTS,
  fieldsForDerivation,
  parseParams,
  stringifyParams,
  validateParams,
  isStaffDerivation,
} from "../../utils/derivation";

const blank = () => ({
  name: "",
  description: "",
  productTypeId: "",
  costBasis: "fixed",
  derivation: "FIXED",
  derivationParamsObj: {},
  defaultMarkupPct: "",
  markupExempt: false,
  isActive: true,
});

const blankPricing = () => ({
  locationId: "",
  seasonId: "",
  slot: "AM_PM",
  unitCost: "",
  sellPrice: "",
  tripCost: "",
  effectiveFrom: new Date().toISOString().slice(0, 10),
  available: true,
  notes: "",
});

function DerivationParamsForm({ derivation, params, onChange }) {
  const fields = fieldsForDerivation(derivation);
  if (fields.length === 0 && !isStaffDerivation(derivation)) {
    return <Typography variant="body2" color="text.secondary">No params required for {derivation}.</Typography>;
  }
  return (
    <Stack spacing={2}>
      {fields.map((f) => {
        if (f.kind === "select") {
          return (
            <FormControl key={f.key} size="small" fullWidth>
              <InputLabel>{f.key}</InputLabel>
              <Select
                label={f.key}
                value={params[f.key] ?? ""}
                onChange={(e) => onChange({ ...params, [f.key]: e.target.value })}
              >
                <MenuItem value="">(none)</MenuItem>
                {f.options.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </Select>
            </FormControl>
          );
        }
        return (
          <TextField
            key={f.key}
            size="small"
            label={f.key + (f.required ? " *" : "")}
            type="number"
            value={params[f.key] ?? ""}
            onChange={(e) => onChange({ ...params, [f.key]: e.target.value })}
            fullWidth
          />
        );
      })}
      {isStaffDerivation(derivation) && (
        <FormControlLabel
          control={
            <Checkbox
              checked={!!params.is_residential}
              onChange={(e) => onChange({ ...params, is_residential: e.target.checked })}
            />
          }
          label="Residential STAFF (counts toward bliss_staff_pax)"
        />
      )}
    </Stack>
  );
}

const SLOT_PLACEABLE_TYPE_CODES = ["ACTIVITY", "TRIP", "TUITION", "TRANSPORT"];

// Pricing rows are AM_PM, FULL_DAY or EVENING; legacy null / "AM" / "PM" rows read as AM_PM.
const pricingSlotLabel = (slot) => (slot === "FULL_DAY" || slot === "EVENING" ? slot : "AM_PM");

export default function ProductDetail({ productId, productTypes, onClose, onCreated }) {
  const [form, setForm] = useState(blank());
  const [pricingRows, setPricingRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [locations, setLocations] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [pricingDraft, setPricingDraft] = useState(null);
  const [pricingDraftId, setPricingDraftId] = useState(null);
  const [pricingSaving, setPricingSaving] = useState(false);

  const isSlotPlaceable = (() => {
    const t = productTypes.find((x) => x.id === Number(form.productTypeId));
    return !!t && SLOT_PLACEABLE_TYPE_CODES.includes(t.code);
  })();

  useEffect(() => {
    listLocations({ activeOnly: false })
      .then((d) => setLocations(Array.isArray(d) ? d : []))
      .catch(() => {});
    listSeasons({ activeOnly: false })
      .then((d) => setSeasons(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const loadProduct = useCallback(async () => {
    if (!productId) return;
    try {
      setLoading(true);
      const p = await getProduct(productId);
      setForm({
        name: p.name || "",
        description: p.description || "",
        productTypeId: p.productTypeId,
        costBasis: p.costBasis || "fixed",
        derivation: p.derivation || "FIXED",
        derivationParamsObj: parseParams(p.derivationParams),
        defaultMarkupPct: p.defaultMarkupPct ?? "",
        markupExempt: !!p.markupExempt,
        isActive: p.isActive !== false,
      });
      setPricingRows(Array.isArray(p.pricingRows) ? p.pricingRows : []);
    } catch (e) {
      setError(e.message || "Failed to load product");
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => { loadProduct(); }, [loadProduct]);

  const locationName = useMemo(() => {
    const m = {};
    locations.forEach((l) => { m[l.id] = l.name; });
    return m;
  }, [locations]);
  const seasonName = useMemo(() => {
    const m = {};
    seasons.forEach((s) => { m[s.id] = s.name; });
    return m;
  }, [seasons]);

  const save = async () => {
    if (!form.name.trim()) { setError("Name is required"); return; }
    if (!form.productTypeId) { setError("Product type is required"); return; }
    const paramsErrors = validateParams(form.derivation, form.derivationParamsObj);
    if (paramsErrors.length) { setError(paramsErrors.join("; ")); return; }
    const body = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      productTypeId: Number(form.productTypeId),
      costBasis: form.costBasis,
      derivation: form.derivation,
      derivationParams: stringifyParams(form.derivationParamsObj, form.derivation),
      defaultMarkupPct: form.defaultMarkupPct === "" ? null : String(form.defaultMarkupPct),
      markupExempt: !!form.markupExempt,
      isActive: !!form.isActive,
    };
    try {
      setSaving(true);
      setError("");
      let saved;
      if (productId) saved = await updateProduct(productId, body);
      else saved = await createProduct(body);
      if (!productId && saved?.id) {
        onCreated?.(saved.id);
        return;
      }
      await loadProduct();
    } catch (e) {
      setError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!productId) return;
    if (!window.confirm(`Delete product "${form.name}"? If quotes or templates use it, it's made inactive instead.`)) return;
    try {
      setError("");
      const result = await deleteProduct(productId);
      if (result?.outcome === "DEACTIVATED") {
        // In use: it stays, now inactive. Say why rather than closing.
        setNotice(result.message);
        setForm((f) => ({ ...f, isActive: false }));
        return;
      }
      onClose();
    } catch (e) {
      setError(e.message || "Delete failed");
    }
  };

  // Pricing
  const openNewPricing = () => { setPricingDraft(blankPricing()); setPricingDraftId(null); };
  const openEditPricing = (row) => {
    // Legacy rows may have slot null / "AM" / "PM" — collapse to "AM_PM" for the new model.
    const slot = pricingSlotLabel(row.slot);
    setPricingDraft({
      locationId: row.locationId ?? "",
      seasonId: row.seasonId ?? "",
      slot,
      unitCost: row.unitCost ?? "",
      sellPrice: row.sellPrice ?? "",
      tripCost: row.tripCost ?? "",
      effectiveFrom: row.effectiveFrom || "",
      available: row.available !== false,
      notes: row.notes || "",
    });
    setPricingDraftId(row.id);
  };
  const closePricing = () => { setPricingDraft(null); setPricingDraftId(null); };

  const savePricing = async () => {
    if (!productId) { setError("Save the product first before adding pricing rows"); return; }
    const body = {
      locationId: pricingDraft.locationId === "" ? null : Number(pricingDraft.locationId),
      seasonId: pricingDraft.seasonId === "" ? null : Number(pricingDraft.seasonId),
      slot: pricingDraft.slot || "AM_PM",
      unitCost: String(pricingDraft.unitCost ?? "0"),
      sellPrice: pricingDraft.sellPrice === "" ? null : String(pricingDraft.sellPrice),
      tripCost: pricingDraft.tripCost === "" ? null : String(pricingDraft.tripCost),
      effectiveFrom: pricingDraft.effectiveFrom,
      available: !!pricingDraft.available,
      notes: pricingDraft.notes || null,
    };
    try {
      setPricingSaving(true);
      if (pricingDraftId) await updatePricing(productId, pricingDraftId, body);
      else await addPricing(productId, body);
      closePricing();
      loadProduct();
    } catch (e) {
      setError(e.message || "Pricing save failed");
    } finally {
      setPricingSaving(false);
    }
  };

  const removePricing = async (row) => {
    if (!window.confirm("Delete this pricing row?")) return;
    try {
      await deletePricing(productId, row.id);
      loadProduct();
    } catch (e) {
      setError(e.message || "Pricing delete failed");
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onClose}>Back</Button>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {productId ? `Edit Product: ${form.name || ""}` : "New Product"}
        </Typography>
        {productId && (
          <Button color="error" onClick={remove}>Delete</Button>
        )}
        <Button variant="contained" onClick={save} disabled={saving || loading}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </Stack>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
      {notice && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice("")}>{notice}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Product</Typography>
              <Stack spacing={2}>
                <TextField
                  label="Name" required fullWidth
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                <TextField
                  label="Description" fullWidth multiline minRows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
                <FormControl size="small" fullWidth>
                  <InputLabel>Product Type</InputLabel>
                  <Select
                    label="Product Type"
                    value={form.productTypeId || ""}
                    onChange={(e) => setForm({ ...form, productTypeId: e.target.value })}
                  >
                    {productTypes.map((t) => (
                      <MenuItem key={t.id} value={t.id}>{t.name} ({t.code})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Cost Basis</InputLabel>
                  <Select
                    label="Cost Basis"
                    value={form.costBasis}
                    onChange={(e) => setForm({ ...form, costBasis: e.target.value })}
                  >
                    {COST_BASES.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Derivation</InputLabel>
                  <Select
                    label="Derivation"
                    value={form.derivation}
                    onChange={(e) => setForm({
                      ...form,
                      derivation: e.target.value,
                      derivationParamsObj: {},
                    })}
                  >
                    {DERIVATIONS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                  </Select>
                </FormControl>
                <Divider />
                <Typography variant="body2" color="text.secondary">Derivation params</Typography>
                <DerivationParamsForm
                  derivation={form.derivation}
                  params={form.derivationParamsObj}
                  onChange={(p) => setForm({ ...form, derivationParamsObj: p })}
                />
                <Divider />
                <TextField
                  label="Default Markup %" type="number"
                  value={form.defaultMarkupPct}
                  onChange={(e) => setForm({ ...form, defaultMarkupPct: e.target.value })}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!form.markupExempt}
                      onChange={(e) => setForm({ ...form, markupExempt: e.target.checked })}
                    />
                  }
                  label="Markup-exempt (passes through at cost regardless of quote markup)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!form.isActive}
                      onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>Pricing rows</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={openNewPricing}
                  disabled={!productId}
                >
                  Add row
                </Button>
              </Stack>
              {!productId && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Save the product first to add pricing rows.
                </Typography>
              )}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Location</TableCell>
                      <TableCell>Season</TableCell>
                      {isSlotPlaceable && <TableCell>Slot</TableCell>}
                      <TableCell>Unit Cost</TableCell>
                      <TableCell>Sell Price</TableCell>
                      <TableCell>Effective From</TableCell>
                      <TableCell>Avail</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pricingRows.length === 0 ? (
                      <TableRow><TableCell colSpan={isSlotPlaceable ? 8 : 7} align="center">No pricing rows</TableCell></TableRow>
                    ) : pricingRows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.locationId ? locationName[row.locationId] || row.locationId : "(any)"}</TableCell>
                        <TableCell>{row.seasonId ? seasonName[row.seasonId] || row.seasonId : "(any)"}</TableCell>
                        {isSlotPlaceable && <TableCell>{pricingSlotLabel(row.slot)}</TableCell>}
                        <TableCell>{row.unitCost}{row.tripCost != null ? ` + ${row.tripCost} / trip` : ""}</TableCell>
                        <TableCell>{row.sellPrice ?? "cost + markup"}</TableCell>
                        <TableCell>{row.effectiveFrom}</TableCell>
                        <TableCell>{row.available ? "Yes" : "No"}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openEditPricing(row)}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => removePricing(row)}><DeleteIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {pricingDraft && (
                <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    {pricingDraftId ? "Edit pricing row" : "New pricing row"}
                  </Typography>
                  <Stack spacing={2}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Location</InputLabel>
                        <Select
                          label="Location"
                          value={pricingDraft.locationId}
                          onChange={(e) => setPricingDraft({ ...pricingDraft, locationId: e.target.value })}
                        >
                          <MenuItem value="">(any)</MenuItem>
                          {locations.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Season</InputLabel>
                        <Select
                          label="Season"
                          value={pricingDraft.seasonId}
                          onChange={(e) => setPricingDraft({ ...pricingDraft, seasonId: e.target.value })}
                        >
                          <MenuItem value="">(any)</MenuItem>
                          {seasons.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Stack>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      {isSlotPlaceable && (
                        <FormControl size="small" fullWidth>
                          <InputLabel>Slot</InputLabel>
                          <Select
                            label="Slot"
                            value={pricingDraft.slot || "AM_PM"}
                            onChange={(e) => setPricingDraft({ ...pricingDraft, slot: e.target.value })}
                          >
                            {SLOTS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                          </Select>
                        </FormControl>
                      )}
                      <TextField
                        size="small" label="Unit Cost" type="number" fullWidth
                        helperText="Per unit, e.g. a ticket per head"
                        value={pricingDraft.unitCost}
                        onChange={(e) => setPricingDraft({ ...pricingDraft, unitCost: e.target.value })}
                      />
                      <TextField
                        size="small" label="Trip Cost (optional)" type="number" fullWidth
                        value={pricingDraft.tripCost}
                        onChange={(e) => setPricingDraft({ ...pricingDraft, tripCost: e.target.value })}
                        helperText="Per trip, e.g. the coach"
                      />
                      <TextField
                        size="small" label="Sell Price (optional)" type="number" fullWidth
                        value={pricingDraft.sellPrice}
                        onChange={(e) => setPricingDraft({ ...pricingDraft, sellPrice: e.target.value })}
                        helperText="Per unit; replaces cost + markup"
                      />
                      <TextField
                        size="small" label="Effective From" type="date" fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={pricingDraft.effectiveFrom}
                        onChange={(e) => setPricingDraft({ ...pricingDraft, effectiveFrom: e.target.value })}
                      />
                    </Stack>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!pricingDraft.available}
                          onChange={(e) => setPricingDraft({ ...pricingDraft, available: e.target.checked })}
                        />
                      }
                      label="Available"
                    />
                    <TextField
                      size="small" label="Notes" fullWidth
                      value={pricingDraft.notes}
                      onChange={(e) => setPricingDraft({ ...pricingDraft, notes: e.target.value })}
                    />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button onClick={closePricing}>Cancel</Button>
                      <Button variant="contained" onClick={savePricing} disabled={pricingSaving}>
                        {pricingSaving ? "Saving…" : "Save row"}
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
